import { useState, useEffect } from "react";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { getCurrencyAmount } from "../../../utils/stringUtils";

import Toaster from "../../../components/common/Toaster";
import journalService from "../../../services/journalService";
import moment from "moment";

const columns = [
  { label: "DATE" },
  { label: "NO." },
  { label: "CUSTOMER" },
  { label: "DESCRIPTION" },
  { label: "GROSS AMOUNT" },
  { label: "DISCOUNT" },
  { label: "WITHHELD AMOUNT" },
  { label: "NET AMOUNT" },
];

const CashReceipts = ({ id }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const { result } = await journalService.getAccountCashReceiptsAsync(id);

        const items = result.receipts.map((item) => {
          item["gross"] = getCurrencyAmount(item.gross_amount);
          item["discount"] = getCurrencyAmount(item.discount_amount);
          item["withheld"] = getCurrencyAmount(item.withheld_amount);
          item["net"] = getCurrencyAmount(item.net_amount);
          item["date_issued"] = item.date_issued
            ? moment(item.date_issued).format("DD-MMM-yyyy")
            : "";

          return item;
        });

        result["items"] = items;

        if (mounted) {
          setData(result);
          setIsLoading(false);
        }
      } catch (ex) {
        setIsLoading(false);
        Toaster.Error("Something went wrong.");
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  const handleExport = async () => {
    try {
      const { result } = await journalService.generateCashReceiptsAsync(id);

      const mediaType =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,";

      const link = document.createElement("a");
      link.href = mediaType + result.content;
      link.download = result.file_name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <div className="card p-6">
      <div className="flex justify-end mb-2 px-5">
        <button
          className="button button-primary w-44 text-white"
          onClick={() => handleExport()}
        >
          <IoCloudDownloadOutline className="mr-2" /> Export to Excel
        </button>
      </div>
      {data && !isLoading && (
        <div className="p-4">
          <div className="mb-10 text-center">
            <p>Dr. {data.name}</p>
            <span className="font-bold text-sm">Cash Receipts Journal</span>
          </div>
          <table className="min-w-full table-fixed">
            <thead>
              <tr className="border-t border-b border-black">
                {columns.map((item, index) => (
                  <th
                    key={index}
                    className="font-semibold text-sm py-1 px-4 text-left border-r border-gray-200 last:border-r-0"
                  >
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items &&
                data.items.map((item, index) => {
                  return (
                    <tr key={index} className="text-sm">
                      <td className="px-4 py-2">{item.date_issued}</td>
                      <td className="px-4 py-2">{item.number}</td>
                      <td className="px-4 py-2">{item.client_name}</td>
                      <td></td>
                      <td className="px-4 py-2 text-right">{item.gross}</td>
                      <td className="px-4 py-2 text-right">{item.discount}</td>
                      <td className="px-4 py-2 text-right">{item.withheld}</td>
                      <td className="px-4 py-2 text-right">{item.net}</td>
                    </tr>
                  );
                })}
              <tr className="text-sm border-t">
                <td className="px-4 font-bold" colSpan={4}>
                  TOTAL
                </td>
                <td className="px-4 py-2 text-right font-bold">
                  {getCurrencyAmount(data.total_gross_amount)}
                </td>
                <td className="px-4 py-2 text-right font-bold">
                  {getCurrencyAmount(data.total_discount_amount)}
                </td>
                <td className="px-4 py-2 text-right font-bold">
                  {getCurrencyAmount(data.total_withheld_amount)}
                </td>
                <td className="px-4 py-2 text-right font-bold">
                  {getCurrencyAmount(data.total_net_amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CashReceipts;
